import { useEffect, useState, useMemo } from 'react';
import { ProTopBar } from '../components/ProTopBar';
import { MessagesThreadList } from '../components/MessagesThreadList';
import { MessagesChatPanel } from '../components/MessagesChatPanel';
import {
  useProMessageThreads,
  useProMessageThread,
  useSendProMessage,
  useMarkProMessageThreadRead,
} from '../lib/queries';
import type { ProMessageThreadForPro } from '../lib/api';

export default function Messaggi() {
  const threadsQuery = useProMessageThreads();
  const threads: ProMessageThreadForPro[] = threadsQuery.data?.threads ?? [];
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const markReadMutation = useMarkProMessageThreadRead();

  // Auto-seleziona il primo thread quando arrivano i dati e nessuno è selezionato.
  useEffect(() => {
    if (!selectedPatientId && threads.length > 0) {
      setSelectedPatientId(threads[0].patient_id);
    }
  }, [selectedPatientId, threads]);

  const threadQuery = useProMessageThread(selectedPatientId, 50);
  const sendMutation = useSendProMessage(selectedPatientId);

  const selectedThread = useMemo(
    () => threads.find((t) => t.patient_id === selectedPatientId) || null,
    [threads, selectedPatientId]
  );

  // Mark-as-read quando si apre un thread con unread.
  // Evita loop: solo se il thread cambia e ha unread_count > 0.
  useEffect(() => {
    if (!selectedPatientId) return;
    const t = threads.find((x) => x.patient_id === selectedPatientId);
    if (t && t.unread_count > 0 && !markReadMutation.isPending) {
      markReadMutation.mutate(selectedPatientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId, threads]);

  const totalUnread = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unread_count || 0), 0),
    [threads]
  );
  const breadcrumb = `Studio Pro · ${threads.length} ${
    threads.length === 1 ? 'conversazione' : 'conversazioni'
  }${totalUnread > 0 ? ` · ${totalUnread} non letti` : ''}`;

  const handleSend = (text: string) => {
    // IIFE per evitare async-in-sync handler (CLAUDE.md feedback).
    void (async () => {
      try {
        await sendMutation.mutateAsync(text);
      } catch (err) {
        // L'errore viene esposto via sendMutation.error; toast opzionale futuro.
        console.error('[messaggi] send error', err);
      }
    })();
  };

  return (
    <>
      <ProTopBar breadcrumb={breadcrumb} title="Messaggi" />
      <div
        style={{
          display: 'flex',
          height: 'calc(100vh - 240px)',
          minHeight: 480,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 16,
          overflow: 'hidden',
          marginTop: 8,
        }}
      >
        <MessagesThreadList
          threads={threads}
          selectedPatientId={selectedPatientId}
          onSelect={setSelectedPatientId}
          isLoading={threadsQuery.isLoading}
          isError={threadsQuery.isError}
        />
        <MessagesChatPanel
          thread={selectedThread}
          messages={threadQuery.data?.messages ?? []}
          isLoading={threadQuery.isLoading}
          isError={threadQuery.isError}
          isSending={sendMutation.isPending}
          onSend={handleSend}
        />
      </div>
    </>
  );
}
